import { isHybridApp } from "src/lib/platform";
import TaskManagementBloc, {
  TaskKey,
  TaskResponseKnown
} from "src/state/TaskManagementBloc/TaskManagementBloc";
import {
  LabPreferenceControllerService,
  LabReportControllerService,
  LabReportUploadRequest,
  TaskResponse
} from "@9amhealth/openapi";
import { Blac, Cubit } from "blac-next";
import reportErrorSentry from "src/lib/reportErrorSentry";
import translate from "src/lib/translate";
import BlockingLoadingOverlayController from "../BlockingLoadingOverlay/BlockingLoadingOverlayController";
import {
  pushNotificationState,
  taskManagementState,
  toast
} from "src/state/state";
import { KnownProgram } from "src/state/ProgramBloc/ProgramBloc";
import {
  AppPopup,
  AppQueryPopupsController
} from "../AppQueryPopups/AppQueryPopupsBloc";
import { LabNames } from "src/constants/labNames";

export enum InitialLabsStep {
  FirstStep = "firstStep",
  Status = "status",
  SkipLabsOption = "skipLabsOption",
  Questionnaire = "questionnaire",
  MedicalInsurance = "medicalInsurance",
  WaitingForLabRequisition = "waitingForLabRequisition"
}

export type InitialLabsStepKey = InitialLabsStep;

export interface InitialLabsStepConfig {
  key: InitialLabsStepKey;
  isAvailable: () => boolean;
}

const WAITING_FALLBACK_TIMEOUT_MS = 120_000; // 2 minutes
const WAITING_POLL_INTERVAL_MS = 5_000; // 5 seconds

export default class InitialLabsBloc extends Cubit<{
  labRequisitionFileId?: string;
  labOrderProviderName?: string;
  activeStep: InitialLabsStepKey;
  showLearnMore: boolean;
  canSkipInitialLabsButton: boolean;
  labsVerified: boolean;
  labsRequired: boolean;
  underReview: boolean;
  showSkipInitialLabsConfirmation: boolean;
  statusTitle: "underReview" | "labsVerified" | "labsRequired";
  showLabStatusOverview: boolean;
  showPushNotificationButton?: boolean;
  questionnaireTaskFinished?: boolean;
  initialLabsTaskFinished?: boolean;
  medicalInsuranceTaskFinished?: boolean;
  uploadReady?: boolean;
  waitingTimedOut?: boolean;
}> {
  loggingEnabled = false;

  questionnaireTask?: TaskResponseKnown;
  labsTask?: TaskResponseKnown;
  medicalInsuranceTask?: TaskResponseKnown;

  uploadedFileIds: string[] = [];
  private waitingTimeout?: ReturnType<typeof setTimeout>;
  private pollingInterval?: ReturnType<typeof setInterval>;
  private hasRedirectedFromWaiting = false;

  get needsNotificationPermission() {
    return (
      isHybridApp() &&
      pushNotificationState.state.permissions?.receive !== "granted"
    );
  }

  startWaitingTimeout = () => {
    this.clearWaitingTimeout();
    this.waitingTimeout = setTimeout(() => {
      this.stopPolling();
      this.patch({ waitingTimedOut: true });
    }, WAITING_FALLBACK_TIMEOUT_MS);
  };

  clearWaitingTimeout = () => {
    if (this.waitingTimeout) {
      clearTimeout(this.waitingTimeout);
      this.waitingTimeout = undefined;
    }
  };

  startPolling = () => {
    this.stopPolling();
    this.hasRedirectedFromWaiting = false;
    this.pollingInterval = setInterval(() => {
      void this.checkForInitialLabTaskUpdates();
    }, WAITING_POLL_INTERVAL_MS);
  };

  stopPolling = () => {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = undefined;
    }
  };

  handleWaitingComplete = () => {
    if (this.needsNotificationPermission) {
      void pushNotificationState
        .requestPermissions({ goToSettings: true })
        .then(() => AppQueryPopupsController.closePopup());
    } else {
      AppQueryPopupsController.closePopup();
    }
  };

  checkForInitialLabTaskUpdates = async () => {
    try {
      await taskManagementState.loadProgramTasks(KnownProgram.ONBOARDING);
      this.updateTasks();
    } catch (e) {
      this.log("Error checking for initial lab task updates", {
        error: e instanceof Error ? e : String(e)
      });
    }
  };

  private redirectFromWaiting = (
    popup: AppPopup,
    params: Record<string, string>
  ) => {
    this.hasRedirectedFromWaiting = true;
    this.clearWaitingTimeout();
    this.stopPolling();
    AppQueryPopupsController.closePopup();
    AppQueryPopupsController.openPopup(popup, { additionalParameters: params });
  };

  private checkWaitingRedirect = (stateCopy: typeof this.state) => {
    if (
      this.state.activeStep !== InitialLabsStep.WaitingForLabRequisition ||
      this.hasRedirectedFromWaiting
    ) {
      return;
    }

    const data = this.labsTask?.additionalData;
    const str = (v: unknown): string | undefined =>
      typeof v === "string" && v !== "" && v !== "undefined" ? v : undefined;
    const appointmentId = str(data?.appointmentId);
    const schedulingUrl = str(data?.appointmentUrl);
    const requisitionFileId = str(stateCopy.labRequisitionFileId);
    const provider = str(data?.labOrderProvider) ?? str(data?.provider);

    if (appointmentId) {
      this.redirectFromWaiting(AppPopup.appointment, { id: appointmentId });
    } else if (schedulingUrl) {
      this.redirectFromWaiting(AppPopup.iframe, {
        url: schedulingUrl,
        title: translate("task.consolidated_labs.schedule_visit")
      });
    } else if (requisitionFileId) {
      const params: Record<string, string> = { fileId: requisitionFileId };
      if (provider) {
        params.provider = provider;
      }
      this.redirectFromWaiting(AppPopup.labInstructions, params);
    }
  };

  private log(message: string, data?: Record<string, unknown>) {
    if (data && data.error) {
      console.error(`[InitialLabsBloc] ${message}`, data);
      return;
    }

    if (!this.loggingEnabled) {
      return;
    }
    if (data) {
      console.log(`[InitialLabsBloc] ${message}`, data);
    } else {
      console.log(`[InitialLabsBloc] ${message}`);
    }
  }

  get steps(): InitialLabsStepConfig[] {
    return [
      {
        key: InitialLabsStep.FirstStep,
        isAvailable: () => true
      },
      {
        key: InitialLabsStep.Status,
        isAvailable: this.stepIsAvailableLabsStatus
      },
      {
        key: InitialLabsStep.SkipLabsOption,
        isAvailable: this.stepIsAvailableSkipLabsOption
      },
      {
        key: InitialLabsStep.Questionnaire,
        isAvailable: this.stepIsAvailableQuestionnaire
      },
      {
        key: InitialLabsStep.MedicalInsurance,
        isAvailable: this.stepIsAvailableMedicalInsurance
      },
      {
        key: InitialLabsStep.WaitingForLabRequisition,
        isAvailable: () => true
      }
    ];
  }

  stepIsAvailableLabsStatus = () => {
    // skip if member already did the labs preferences questionnaire
    if (this.questionnaireTaskCompleted) {
      return false;
    }
    return true;
  };

  stepIsAvailableSkipLabsOption = () => {
    // skip if member already did the labs preferences questionnaire
    if (this.questionnaireTaskCompleted) {
      return false;
    }
    return this.labsTask?.status !== TaskResponse.status.SKIPPED;
  };

  stepIsAvailableQuestionnaire = () => {
    // skip if member already did the labs preferences questionnaire
    if (this.questionnaireTaskCompleted) {
      return false;
    }
    return !this.selectedSkipLabs;
  };

  stepIsAvailableMedicalInsurance = () => {
    const { isTaskStatusFinal } = Blac.getBloc(TaskManagementBloc);
    return (
      Boolean(this.medicalInsuranceTask) &&
      !isTaskStatusFinal(this.medicalInsuranceTask)
    );
  };

  stepsWithFloatingContinueButton: InitialLabsStepConfig["key"][] = [
    InitialLabsStep.Status
  ];

  get showFloatingContinueButton() {
    const { activeStep } = this.state;
    return this.stepsWithFloatingContinueButton.includes(activeStep);
  }

  constructor() {
    super({
      activeStep: InitialLabsStep.FirstStep,
      showLearnMore: false,
      canSkipInitialLabsButton: false,
      showSkipInitialLabsConfirmation: false,
      statusTitle: "labsRequired",
      labsVerified: false,
      labsRequired: false,
      underReview: false,
      showLabStatusOverview: false
    });
    this.log("constructor, initial step: firstStep");
    this.updateTasks();
  }

  setUploadedFileIds = (fileIds: string[]) => {
    this.uploadedFileIds = fileIds;
  };

  setUploadReady = (ready: boolean) => {
    this.patch({ uploadReady: ready });
  };

  submitLabReport = () => {
    if (
      this.state.activeStep === InitialLabsStep.Status &&
      this.uploadedFileIds.length > 0
    ) {
      void LabReportControllerService.uploadLabReport({
        fileIds: this.uploadedFileIds,
        provider: LabReportUploadRequest.provider.PATIENT
      })
        .then(() => {
          this.uploadedFileIds = [];
        })
        .catch((e) => {
          reportErrorSentry(new Error("Failed to upload lab report"), {
            extra: {
              error: e instanceof Error ? e : String(e),
              fileIds: this.uploadedFileIds
            }
          });
        });
    }
  };

  completeCurrentStep = () => {
    this.log(`completeCurrentStep at "${this.state.activeStep}"`);
    this.submitLabReport();
    this.nextStep();
  };

  handleQuestionnaireSubmit = () => {
    if (this.state.activeStep !== InitialLabsStep.Questionnaire) {
      return;
    }
    this.log(`handleQuestionnaireSubmit at "${this.state.activeStep}"`);
    void taskManagementState.loadProgramTasks(KnownProgram.ONBOARDING);
    this.updateTasks();
    this.nextStep();
  };

  handleMedicalInsuranceSaved = () => {
    if (this.state.activeStep !== InitialLabsStep.MedicalInsurance) {
      this.log(
        `handleMedicalInsuranceSaved skipped, active step is "${this.state.activeStep}"`
      );
      return;
    }
    this.log(`handleMedicalInsuranceSaved at "${this.state.activeStep}"`);
    void taskManagementState.loadProgramTasks(KnownProgram.ONBOARDING);
    this.updateTasks();
  };

  private logStepChange(
    caller: string,
    from: string,
    to: string,
    extra?: Record<string, unknown>
  ) {
    this.log(`${caller}: ${from} → ${to}`, {
      availableSteps: this.steps
        .filter((s) => s.isAvailable())
        .map((s) => s.key),
      ...extra
    });
  }

  nextStep = () => {
    const available = this.steps.filter((s) => s.isAvailable());
    const currentIdx = available.findIndex(
      (s) => s.key === this.state.activeStep
    );
    const next = available[currentIdx + 1] as InitialLabsStepConfig | undefined;
    if (next) {
      this.logStepChange("nextStep", this.state.activeStep, next.key);
      this.patch({ activeStep: next.key });
    } else {
      this.log(
        `nextStep: no next step from "${this.state.activeStep}" (index ${currentIdx}/${available.length - 1})`
      );
    }
  };

  prevStep = () => {
    const available = this.steps.filter((s) => s.isAvailable());
    const currentIdx = available.findIndex(
      (s) => s.key === this.state.activeStep
    );
    const prev = available[currentIdx - 1] as InitialLabsStepConfig | undefined;
    if (prev) {
      this.logStepChange("prevStep", this.state.activeStep, prev.key);
      this.patch({ activeStep: prev.key });
    } else {
      this.log(
        `prevStep: no prev step from "${this.state.activeStep}" (index ${currentIdx})`
      );
    }
  };

  selectedSkipLabs = false;
  setSkipLabs = (skip: boolean) => {
    this.log(`setSkipLabs: ${skip}`);
    this.selectedSkipLabs = skip;
  };

  updateTasks = () => {
    const { getTask, isTaskStatusFinal } = Blac.getBloc(TaskManagementBloc);
    this.questionnaireTask = getTask(TaskKey.INITIAL_LAB_QUESTIONNAIRE);
    this.labsTask = getTask(TaskKey.COMPLETE_INITIAL_LAB_ORDER);
    this.medicalInsuranceTask = getTask(TaskKey.MEDICAL_INSURANCE);

    this.log("updateTasks", {
      currentStep: this.state.activeStep,
      questionnaire: this.questionnaireTask?.status,
      labs: this.labsTask?.status,
      medicalInsurance: this.medicalInsuranceTask?.status,
      labsAdditionalData: this.labsTask?.additionalData
    });

    const labOrderProvider = (this.labsTask?.additionalData?.labOrderProvider ??
      this.labsTask?.additionalData?.provider) as string | undefined;

    this.updateState({
      questionnaireTaskFinished: isTaskStatusFinal(this.questionnaireTask),
      initialLabsTaskFinished: isTaskStatusFinal(this.labsTask),
      medicalInsuranceTaskFinished: isTaskStatusFinal(
        this.medicalInsuranceTask
      ),
      labRequisitionFileId: this.labsTask?.additionalData?.requisitionFileId as
        | string
        | undefined,
      labOrderProviderName: labOrderProvider
        ? LabNames[labOrderProvider as keyof typeof LabNames]
        : undefined
    });
  };

  updateState = (taskState: Partial<typeof this.state>) => {
    const stateCopy = { ...this.state, ...taskState };

    // Recalculate active step based on current availability
    const available = this.steps.filter((s) => s.isAvailable());
    const isCurrentStepAvailable = available.some(
      (s) => s.key === stateCopy.activeStep
    );

    if (!isCurrentStepAvailable) {
      const currentIdx = this.steps.findIndex(
        (s) => s.key === stateCopy.activeStep
      );
      const next = this.steps.find(
        (s, i) => i >= currentIdx && s.isAvailable()
      );
      const newStep = next?.key ?? InitialLabsStep.Status;
      this.logStepChange(
        "updateState (step unavailable)",
        stateCopy.activeStep,
        newStep
      );
      stateCopy.activeStep = newStep;
    }

    stateCopy.showPushNotificationButton = isHybridApp();

    stateCopy.labsVerified = Boolean(taskState.initialLabsTaskFinished);

    stateCopy.canSkipInitialLabsButton =
      this.questionnaireTask?.behavior?.userCanSkip === true;
    stateCopy.labsRequired = this.initialLabsTaskStatusCanBeSkipped;
    stateCopy.underReview = this.initialLabsTaskStatusUnderReview;

    stateCopy.showLabStatusOverview =
      stateCopy.labsRequired || stateCopy.labsVerified || stateCopy.underReview;

    if (stateCopy.labsVerified) {
      stateCopy.statusTitle = "labsVerified";
    } else if (stateCopy.labsRequired) {
      stateCopy.statusTitle = "labsRequired";
    } else if (stateCopy.underReview) {
      stateCopy.statusTitle = "underReview";
    }

    this.checkWaitingRedirect(stateCopy);

    this.emit(stateCopy);
  };

  setShowLearnMore = (show: boolean) => {
    this.patch({ showLearnMore: show });
  };

  get initialLabsTaskStatusUnderReview() {
    const status = this.labsTask?.status;
    return status === TaskResponse.status.LOCKED;
  }

  get initialLabsTaskStatusCanBeSkipped() {
    const status = this.labsTask?.status;
    return (
      status === TaskResponse.status.AVAILABLE ||
      status === TaskResponse.status.IN_PROGRESS
    );
  }

  get questionnaireTaskCompleted(): boolean {
    const { isTaskStatusFinal } = Blac.getBloc(TaskManagementBloc);
    return isTaskStatusFinal(this.questionnaireTask);
  }

  get currentStepIndex(): number {
    const available = this.steps.filter((s) => s.isAvailable());
    return available.findIndex((s) => s.key === this.state.activeStep);
  }

  skipInitialLabs = async () => {
    this.log(`skipInitialLabs at "${this.state.activeStep}"`);
    BlockingLoadingOverlayController.startLoading({
      bg: "transparent"
    });
    try {
      await LabPreferenceControllerService.pauseLabCadenceForOnboarding();
      // reload the tasks for the onboarding program
      await taskManagementState.loadProgramTasks(KnownProgram.ONBOARDING);
      // close the info and confirmation dialog
      this.patch({
        showSkipInitialLabsConfirmation: false,
        showLearnMore: false
      });
      // then close the popup
      AppQueryPopupsController.closePopup();
    } catch (e: unknown) {
      toast.error("error.skipInitialLabs");
      reportErrorSentry(e);
    } finally {
      BlockingLoadingOverlayController.endLoading();
    }
  };

  setShowSkipInitialLabsConfirmation = (show: boolean) => {
    this.patch({ showSkipInitialLabsConfirmation: show });
  };
}
